// Pure dispatch hook over the workspace urgency context + opt-in resolver
// registry (D1). Returns a `ModeUrgency` value the chrome surfaces use to
// drive `<ModeUrgencyBadge />`.
//
// Surface contract:
//   - When no provider is mounted, when the mode has no registered resolver,
//     or when the resolver opts out, the hook returns `{ active: false }`.
//   - A 60s `nowMs` tick (matches the `useAdsHeaderActivity` polling cadence)
//     keeps the badge honest as follow-ups roll into the amber window
//     mid-session without forcing a refetch.

import { useEffect, useMemo, useState } from "react";
import { useAuthOptional } from "@/contexts/AuthContext";
import { useWorkspaceUrgencyContext } from "@/contexts/WorkspaceUrgencyContext";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { MODE_URGENCY_RESOLVERS } from "@/components/shell/modes/modeUrgencyResolvers";
import { NO_URGENCY, type ModeUrgency } from "@/components/shell/modes/modeUrgencyTypes";

const DEFAULT_NOW_TICK_MS = 60_000;

/**
 * Returns a clock value (`Date.now()`) that re-renders every `intervalMs`.
 * Intentionally not memoised across consumers — each call site owns its own
 * timer so unmounting the hook cleans up cleanly.
 */
export function useNowMs(intervalMs: number = DEFAULT_NOW_TICK_MS): number {
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    if (typeof window === "undefined") return;
    const id = window.setInterval(() => setNowMs(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return nowMs;
}

export function useModeUrgency(mode: PrimaryMode): ModeUrgency {
  const ctx = useWorkspaceUrgencyContext();
  const auth = useAuthOptional();
  const nowMs = useNowMs();

  return useMemo<ModeUrgency>(() => {
    if (!ctx) return NO_URGENCY;
    const resolver = MODE_URGENCY_RESOLVERS[mode];
    if (!resolver) return NO_URGENCY;
    try {
      return resolver({ leads: ctx.leads, adsActivity: ctx.adsActivity, auth, nowMs });
    } catch {
      return NO_URGENCY;
    }
  }, [ctx, auth, nowMs, mode]);
}
